// Offline booking queues: orders are saved on the device and synced when internet returns.
// Rule (owner): a booking is CONFIRMED only when the server accepts it; entries synced after the show closes are rejected.
// Item status: pending → syncing → (confirmed | failed). Unsynced items are NEVER treated as confirmed.
const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const emit = () => window.dispatchEvent(new Event("rl-offline-queue"));

export const isNetworkError = (e) => !e?.response || e.code === "ERR_NETWORK" || e.message === "Network Error";
const sig = (o) => JSON.stringify([o.customer_id || "", (o.items || []).map((i) => [i.game_id, i.slat_id, i.number, i.quantity, i.mode || "direct"]).sort()]);

function makeQueue(KEY, FAILED, DONE, endpoint, toBody) {
  let syncing = false;
  const setStatus = (key, status) => write(KEY, read(KEY).map((x) => (x.idempotency_key === key ? { ...x, status } : x)));
  const q = {
    list: () => read(KEY),
    failed: () => read(FAILED),
    confirmed: () => read(DONE),
    /* Returns {ok:true} or {ok:false, reason:"duplicate"} — same customer + same numbers already waiting. */
    add: (order) => {
      const s = sig(order);
      if (read(KEY).some((x) => sig(x) === s)) return { ok: false, reason: "duplicate" };
      write(KEY, [...read(KEY), { ...order, entered_at: new Date().toISOString(), offline: true, status: "pending" }]);
      emit();
      return { ok: true };
    },
    remove: (key) => { write(KEY, read(KEY).filter((x) => x.idempotency_key !== key)); emit(); },
    clearFailed: () => { write(FAILED, []); emit(); },
    clearConfirmed: () => { write(DONE, []); emit(); },
    isSyncing: () => syncing,
    async sync(api) {
      if (syncing || !navigator.onLine) return { synced: 0, failed: 0 };
      syncing = true;
      let synced = 0, failed = 0;
      try {
        for (const o of read(KEY)) {
          setStatus(o.idempotency_key, "syncing"); emit();
          try {
            const { data } = await api.post(endpoint, toBody(o));
            write(KEY, read(KEY).filter((x) => x.idempotency_key !== o.idempotency_key));
            write(DONE, [{ ...o, status: "confirmed", order_id: data?.order_id, server_total: data?.total, synced_at: new Date().toISOString() }, ...read(DONE)].slice(0, 20));
            synced += 1;
          } catch (e) {
            if (isNetworkError(e)) { setStatus(o.idempotency_key, "pending"); break; } // still offline — keep everything
            write(KEY, read(KEY).filter((x) => x.idempotency_key !== o.idempotency_key));
            const d = e.response?.data?.detail;
            write(FAILED, [{ ...o, status: "failed", reason: typeof d === "string" ? d : (d?.[0]?.msg || "Rejected by server"), failed_at: new Date().toISOString() }, ...read(FAILED)].slice(0, 30));
            failed += 1;
          }
          emit();
        }
      } finally { syncing = false; emit(); }
      return { synced, failed };
    },
    startAutoSync(api, onDone) {
      const run = () => q.sync(api).then((r) => { if (r.synced || r.failed) onDone?.(r); });
      window.addEventListener("online", run);
      const t = setInterval(run, 30000);
      run();
      return () => { window.removeEventListener("online", run); clearInterval(t); };
    },
  };
  return q;
}

export const agentQueue = makeQueue("rl_agent_offline_queue", "rl_agent_offline_failed", "rl_agent_offline_done", "/agent/book",
  (o) => ({ customer_id: o.customer_id, idempotency_key: o.idempotency_key, items: o.items, offline: true, entered_at: o.entered_at }));
export const customerQueue = makeQueue("rl_customer_offline_queue", "rl_customer_offline_failed", "rl_customer_offline_done", "/bets/place",
  (o) => ({ idempotency_key: o.idempotency_key, items: o.items, offline: true, entered_at: o.entered_at }));

// backwards-compatible names used by AgentBook / syncReminder
export const queueList = agentQueue.list;
export const failedList = agentQueue.failed;
export const enqueue = agentQueue.add;
export const clearFailed = agentQueue.clearFailed;
export const syncQueue = (api) => agentQueue.sync(api);
export const startAutoSync = (api, onDone) => agentQueue.startAutoSync(api, onDone);
